import _ from 'lodash';
import React from 'react';
import SidebarContainer from 'src/modules/Routing/Sidebar/SidebarContainer';
import { RoutesModules } from 'src/modules/Routing/types';
import { Col, Row, Label } from 'reactstrap';
import InfiniteScroll from 'react-infinite-scroller';
import { MatchesService } from 'src/share/services/matches';
import WebiqMatchCard from './WebiqMatchCard';
import {
  IWebiqMatches,
  IMatchesViewProps,
  initialMatchResults,
  initialState,
  FetchType,
  IRequestMatches,
  IRange,
} from './types';
import * as ReactGA from 'react-ga';
import { toastr } from 'react-redux-toastr';
import { connect } from 'react-redux';
import { IReduxState } from 'src/reduxTypes';
import DateRange from 'src/share/components/DateRange/DateRange';
import { momentToMiliseconds } from 'src/share/utils/utils';
import { MatchesCardLoader } from 'src/share/components/ChartLoader/MatchesCardLoader';

export class WebiqMatches extends React.PureComponent<
  IMatchesViewProps,
  IWebiqMatches
> {
  private clearRequest: IRequestMatches;
  constructor(props: IMatchesViewProps) {
    super(props);
    this.state = _.cloneDeep(initialState);
    this.clearRequest = this.getSearchRequest();
  }

  public async componentDidMount(): Promise<void> {
    await this.fetchData(
      this.props.location.search ? FetchType.History : FetchType.Initial
    );
  }

  public async componentDidUpdate(prevProps: IMatchesViewProps): Promise<void> {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.setState(_.cloneDeep(initialState));
      await this.fetchData(
        this.props.location.search ? FetchType.History : FetchType.Initial
      );
    }
  }

  public getRange(): IRange | null {
    const range = {} as IRange;
    if (
      this.state.dateRange &&
      this.state.dateRange.startDate &&
      this.state.dateRange.endDate &&
      !this.state.dateRange.focus
    ) {
      range.createdAt = {
        gte: momentToMiliseconds(this.state.dateRange.startDate),
        lte: momentToMiliseconds(this.state.dateRange.endDate),
      };
    }
    return Object.keys(range).length > 0 ? range : null;
  }

  public getSearchRequest(
    type: FetchType = FetchType.Initial
  ): IRequestMatches {
    const isSort = type === FetchType.Sort;
    const size = this.state.data.matches.results.length;
    return {
      page: isSort ? 0 : size,
      size: 12,
      sort: this.state.sort,
      range: this.getRange(),
    };
  }

  public async fetchData(type: FetchType): Promise<void> {
    this.setState({ fetchingData: true });
    const isInfinite = type === FetchType.Infinite;
    const request = this.getSearchRequest();
    if (!isInfinite) {
      this.setState({ data: _.cloneDeep(initialMatchResults), loading: true });
    }
    try {
      const respMatches = await MatchesService.getWebiq(request);
      const pages = Math.ceil(respMatches.matches.total / this.state.size);
      this.setState((state) => ({
        data: {
          matches: {
            results: [
              ...state.data.matches.results,
              ...respMatches.matches.results,
            ],
            total: respMatches.matches.total,
          },
        },
        fetchingData: false,
        loading: false,
        pages,
      }));
    } catch (error) {
      this.setState({ loading: false });
    }
  }

  public loadMore = (e): void => {
    if (
      this.state.data.matches.results.length < this.state.data.matches.total &&
      e >= 1 &&
      !this.state.fetchingData
    ) {
      this.fetchData(FetchType.Infinite);
    }
  };

  public deleteMatch = (id: string) => async (): Promise<void> => {
    try {
      this.setState({ deleting: true });
      await MatchesService.deleteWebiqMatchById(id);
      this.setState((state) => ({
        data: {
          ...state.data,
          matches: {
            ...state.data.matches,
            total: state.data.matches.total - 1,
            results: [
              ...state.data.matches.results.filter(
                (match) => match.matchId !== id
              ),
            ],
          },
        },
        deleting: false,
      }));
      ReactGA.event({
        category: 'Match',
        action: 'Discard match',
        label: 'Matches management',
      });
    } catch (error) {
      this.setState({ loading: false, deleting: false });
      toastr.error('There was a problem', 'The match could not be discard');
    }
  };

  public loader(length): JSX.Element {
    return (
      <Row>
        {[...Array(length).keys()].map((data, index) => (
          <Col
            key={index}
            xl="4"
            lg="6"
            md="12"
            sm="12"
            xs="12"
            className="card-column"
          >
            <MatchesCardLoader />
          </Col>
        ))}
      </Row>
    );
  }

  private isFiltering(): boolean {
    return (
      JSON.stringify(_.omit(this.clearRequest, ['page'])) !==
      JSON.stringify(_.omit(this.getSearchRequest(), ['page']))
    );
  }

  public clearSearch = async () => {
    await new Promise((res) =>
      this.setState({ ..._.cloneDeep(initialState) }, res)
    );
    this.fetchData(FetchType.Initial);
  };

  public onDatesChange = (date) => {
    this.setState(
      (state) => ({ ...state, dateRange: { ...state.dateRange, ...date } }),
      () => {
        if (
          !this.state.dateRange.focus &&
          this.state.dateRange.startDate &&
          this.state.dateRange.endDate &&
          (date.startDate || date.endDate)
        ) {
          this.fetchData(FetchType.Filter);
        }
      }
    );
  };

  public render(): JSX.Element {
    return (
      <>
        <div className="filters-box matches">
          <div className="results-count">
            Results: {this.state.data.matches.total}{' '}
            {this.state.data.matches.total === 1 ? 'match' : 'matches'}
          </div>
          <Label className={'dateLabel'}>Date filter</Label>
          <DateRange
            onDatesChange={this.onDatesChange}
            onFocusChange={this.onDatesChange}
            focusedInput={this.state.dateRange.focus}
            startDate={this.state.dateRange.startDate}
            endDate={this.state.dateRange.endDate}
          />
          {this.isFiltering() && (
            <div className="clr-search">
              <b onClick={this.clearSearch}>Clear search</b>
            </div>
          )}
        </div>
        <div className="matches-view">
          <InfiniteScroll
            pageStart={0}
            loadMore={this.loadMore}
            hasMore={this.state.page <= this.state.pages}
            useWindow={false}
          >
            {this.state.loading ? (
              this.loader(12)
            ) : (
              <Row style={{ margin: '0' }}>
                {!this.state.data.matches.results.length ? (
                  <h4 className="no-results">No data found</h4>
                ) : (
                  this.state.data.matches.results.map((match) => (
                    <WebiqMatchCard
                      key={match.matchId}
                      matchData={match}
                      history={this.props.history}
                      deleteMatch={this.deleteMatch}
                      deleting={this.state.deleting}
                    />
                  ))
                )}
              </Row>
            )}
          </InfiniteScroll>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state: IReduxState) => ({
  auth: state.auth,
});

export default SidebarContainer(RoutesModules.SEARCH)(
  connect(mapStateToProps)(WebiqMatches)
);



// WEBPACK FOOTER //
// ./src/modules/AdHocTools/Webiq/WebiqMatches.tsx